import { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, TrendingUp, TrendingDown, Minus, Info } from "lucide-react";
import { KPICircle } from "./KPICircle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOTIVATIONS_LOW = [
  "Salg og opgraderingsydelser handler om at yde en god kundeservice — det er en vigtig del af at være god i sin branche.",
  "Hver behandling er en mulighed for at gøre noget ekstra for kunden. Det er der, opgraderinger opstår.",
  "Den bedste sælger er én der lytter til kunden og anbefaler det rigtige. Det kan du lære dig.",
  "Produktsalg er ikke at presse kunden — det er at hjælpe dem med at tage den bedste beslutning for sig selv.",
  "Store resultater starter med små vaner. Spørg én kunde mere i dag end i går.",
  "Du er her for at lære faget til bunds — og det inkluderer kundeservice og mersalg.",
  "Den faglige ekspertise du opbygger her, er præcis det kunden betaler for. Stå ved den.",
  "At anbefale en opgradering er en service — ikke et salg. Kunden sætter pris på din viden.",
  "Produktviden er en del af din uddannelse. Jo mere du ved, jo lettere er det at anbefale.",
  "Kundeservice i verdensklasse er det, der skiller de gode fra de rigtig gode. Du er her for at blive rigtig god.",
  "Det koster ingenting at spørge — og kunden sætter altid pris på en god anbefaling.",
  "Fokuser på at forstå kundens behov, så følger salget naturligt med.",
  "Din rolle er at give kunden den bedste oplevelse muligt — det er præcis hvad opgraderinger handler om.",
  "En god kosmetolog eller frisør er også en god rådgiver. Brug din viden aktivt.",
  "Mersalg starter med interesse for kunden. Vær nysgerrig — spørg ind til hvad de har brug for.",
];

const MOTIVATIONS_HIGH = [
  "Du er en stjerne-elev — du har forstået hvad god kundeservice er. Fortsæt det gode arbejde!",
  "Du har ligget i toppen — stærkt arbejde! Det er tydeligt at du tager din uddannelse seriøst.",
  "Du performer på et højt niveau og det viser sig i tallene. Imponerende!",
  "Du er beviset på at faglig stolthed og salg går hånd i hånd. Hold det niveau!",
  "Dine resultater taler for sig selv — du forstår hvad det vil sige at give kunden en god oplevelse.",
  "Du er det eksempel andre elever kan lære af. Stærkt!",
  "Du har vist at du kan — nu handler det om at blive ved. Du har styr på det.",
  "Toppen er et godt sted at være. Du har gjort dig fortjent til at stå der.",
  "Dine kunder mærker din entusiasme og ekspertise. Det er præcis det det handler om.",
  "Du leverer resultaterne og det er ikke tilfældigt — det er et produkt af din indsats.",
  "Ikke mange elever kommer op på dit niveau så hurtigt. Virkelig flot arbejde!",
  "Du har forstået sammenhængen mellem god service og gode resultater. Det er en vigtig læring.",
  "Stærk præstation! Du viser hvad der er muligt når man giver den en ekstra skalle.",
  "Du er en af dem der gør en forskel — for kunderne og for holdet.",
  "Du har fundet din groove — fortsæt med at bygge ovenpå det du allerede er god til.",
];

function getMotivationText(overallScore) {
  const day = new Date().getDate() + new Date().getMonth() * 31;
  if (overallScore >= 70) {
    return MOTIVATIONS_HIGH[day % MOTIVATIONS_HIGH.length];
  }
  return MOTIVATIONS_LOW[day % MOTIVATIONS_LOW.length];
}

function fmt(n) {
  return new Intl.NumberFormat("da-DK", {
    style: "currency", currency: "DKK",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function GoalBar({ value, goal1, goal2, format = "pct" }) {
  const max = goal2 * 1.15;
  const pct1 = Math.min((goal1 / max) * 100, 100);
  const pct2 = Math.min((goal2 / max) * 100, 100);
  const valuePct = Math.min((value / max) * 100, 100);
  const reachedGoal2 = value >= goal2;
  const reachedGoal1 = value >= goal1;
  const barColor = reachedGoal2 ? "#22C55E" : reachedGoal1 ? "#22C55E" : value > 0 ? "#EAB308" : "#3F3F46";

  return (
    <div className="space-y-0">
      <div className="relative w-full bg-secondary rounded-full h-2.5 overflow-visible">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${valuePct}%`, background: barColor, boxShadow: reachedGoal2 ? "0 0 8px #22C55E88" : "none" }}
        />
        <div className="absolute top-0 bottom-0 w-px bg-foreground/25" style={{ left: `${pct1}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-foreground/25" style={{ left: `${pct2}%` }} />
      </div>
    </div>
  );
}

function BonusCard({ reached, label, goal, format = "pct", extra }) {
  const displayGoal = format === "kr" ? fmt(goal) : `${goal}%`;
  return (
    <div className={`rounded-xl p-3 border transition-all duration-300 ${reached ? "border-green-500/40 bg-green-500/10" : "border-border bg-secondary"}`}>
      <div className="flex items-center gap-2">
        {reached
          ? <Sparkles className="w-4 h-4 text-green-400 shrink-0 animate-pulse" />
          : <div className="w-4 h-4 rounded-full border border-border shrink-0" />
        }
        <div>
          <p className={`text-[13px] font-bold ${reached ? "text-green-700 dark:text-green-300" : "text-muted-foreground"}`}>
            {reached ? "Opnået" : `${label}: ${displayGoal}`}{extra ? ` ${extra}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

function KPICard({ title, children, reached }) {
  return (
    <div className={`rounded-2xl p-4 border bg-card ${reached ? "border-green-500/30" : "border-border"}`}>
      <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">{title}</p>
      {children}
    </div>
  );
}

function TrendIndicator({ score, reachedGoal1, reachedGoal2 }) {
  if (reachedGoal2) return (
    <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-[13px] font-semibold">
      <Sparkles className="w-3 h-3" /> Tillykke! Du har nået dit 2. delmål!
    </div>
  );
  if (reachedGoal1) return (
    <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-[13px] font-semibold">
      <Sparkles className="w-3 h-3" /> Tillykke! Du har nået dit 1. delmål!
    </div>
  );
  if (score >= 80) return (
    <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-[13px] font-semibold">
      <TrendingUp className="w-3 h-3" /> Du performer stærkt
    </div>
  );
  if (score >= 50) return (
    <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 text-[13px] font-semibold">
      <Minus className="w-3 h-3" /> På vej frem
    </div>
  );
  return (
    <div className="flex items-center gap-1 text-red-600 dark:text-red-400 text-[13px] font-semibold">
      <TrendingDown className="w-3 h-3" /> Fokuser på dine mål
    </div>
  );
}


const PERIODS = [
  { key: "current",    label: "Denne måned" },
  { key: "last_month", label: "Sidste måned" },
  { key: "prev_month", label: "Måneden før" },
];

export function StudentGoalView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("current");

  useEffect(() => {
    setLoading(true);
    setData(null);
    axios.get(`${API}/dashboard/student/kpi?period=${selectedPeriod}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [token, selectedPeriod]);

  if (loading) return (
    <div className="flex justify-center items-center py-24">
      <div className="w-8 h-8 border-2 border-[#FF2A6D] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (error) return (
    <div className="flex justify-center items-center py-24">
      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
    </div>
  );
  if (!data) return null;

  const { kpi, overall, period, uddannelse_type } = data;
  const att = kpi.attendance;
  const upg = kpi.upgrades;
  const prod = kpi.products;
  const gv = kpi.giftvoucher;
  const overallScore = overall.score;

  // Samlet præstation — antal opnåede delmål (0/1/2). Backend afgør hvilken
  // regel der gælder ud fra periode-start (ny logik fra 2026-06-01).
  const samletReachedGoal1 = overall.reached_goal_1 ?? false;
  const samletReachedGoal2 = overall.reached_goal_2 ?? false;
  const samletRegel = overall.regel || "gammel";
  const samletRegelForklaring = samletRegel === "ny"
    ? "Ny regel (fra 1. juni 2026): Begge produktsalg-delmål, eller produktsalg-delmål 1 + opgraderinger/fremmøde + gavekort-delmål 1, udløser begge Samlet-delmål."
    : "Gammel regel: Samlet præstation beregnes ud fra vægtet gennemsnit af alle kategorier (mål 1 ved 50 %, mål 2 ved 80 %).";

  const isKos = uddannelse_type === "kos";

  const motivationText = getMotivationText(overallScore);

  const upgHasData = upg.has_data;
  const upgPct = upg.pct ?? 0;

  // Cirkel-pct: goal_1 = 50% af ringen, goal_2 = 100% (matcher backend scoring)
  function circleScore(value, goal1, goal2) {
    if (goal2 <= 0) return 0;
    if (value >= goal2) return 100;
    if (value >= goal1) return 50 + ((value - goal1) / (goal2 - goal1)) * 50;
    return goal1 > 0 ? (value / goal1) * 50 : 0;
  }
  const attCirclePct = circleScore(att.pct, att.goal_1, att.goal_2);
  const upgCirclePct = upgHasData ? circleScore(upgPct, upg.goal_1, upg.goal_2) : 0;
  const prodCirclePct = circleScore(prod.revenue, prod.goal_1, prod.goal_2);
  const gvCirclePct = circleScore(gv.revenue, gv.goal_1, gv.goal_2);

  // Goal-dot positioner på ringen: goal_1 = 50%, goal_2 = 100%
  const attGoal1RingPct = 50;
  const upgGoal1RingPct = 50;
  const prodGoal1RingPct = 50;
  const gvGoal1RingPct = 50;

  const attColorForBar = att.reached_goal_1 ? "#22C55E" : att.pct >= att.goal_1 / 2 ? "#EAB308" : "#EF4444";

  const periodLabel = period
    ? `${new Date(period.start).toLocaleDateString("da-DK", { day: "numeric", month: "long" })} – ${new Date(new Date(period.end) - 1).toLocaleDateString("da-DK", { day: "numeric", month: "long" })}`
    : null;

  return (
    <div className="space-y-6 animate-fade-in-up opacity-0 stagger-1">

      {/* Period selector */}
      <div className="flex gap-2 flex-wrap">
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setSelectedPeriod(p.key)}
            className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-all ${
              selectedPeriod === p.key
                ? "bg-[#FF2A6D] border-[#FF2A6D] text-white"
                : "bg-secondary border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Samlet info: periode + gavekort-beløb + forklaring */}
      <div className="rounded-2xl border border-[#FF2A6D]/20 bg-[#FF2A6D]/5 p-4 sm:p-5">
        <h3 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-[#FF2A6D]" /> Din mål-periode
        </h3>
        {periodLabel && (
          <p className="text-[13px] text-foreground/90 mb-3">
            <span className="font-semibold">Periode:</span> {periodLabel}
          </p>
        )}
        {selectedPeriod === "current" ? (
          <>
            <p className="text-[13px] text-foreground/80 mb-3 leading-relaxed">
              Når du når dit 1. delmål, udløses et gavekort til TNBC.dk på <span className="font-semibold">{isKos ? "300 kr." : "200 kr."}</span>{" "}
              Når du når dit 2. delmål, udløses yderligere <span className="font-semibold">300 kr.</span> i gavekort.
            </p>
            <p className="text-[13px] text-muted-foreground leading-relaxed mb-3">
              Det er den <span className="font-semibold">store cirkel (Samlet præstation)</span> der afgør om du har nået dit mål og udløser gavekort.
              De små cirkler viser fremgangen på hver kategori, men er ikke selvstændige gavekort-udløsere.
            </p>
            <p className="text-[13px] italic text-foreground/60">"{motivationText}"</p>
          </>
        ) : (
          <p className="text-[13px] text-muted-foreground">Historisk visning — afsluttet periode.</p>
        )}
      </div>

      {/* Samlet præstation */}
      <div className="flex flex-col items-center gap-2">
        <div title={samletRegelForklaring} className="cursor-help">
          <KPICircle
            mode="simple"
            goalMode={true}
            label="Samlet præstation"
            centerValue={`${Math.round(overallScore)}%`}
            percentage={overallScore}
            goal1Pct={50}
            goal2Pct={80}
            reachedGoal1={samletReachedGoal1}
            reachedGoal2={samletReachedGoal2}
            large={true}
          />
        </div>
        <TrendIndicator
          score={overallScore}
          reachedGoal1={samletReachedGoal1}
          reachedGoal2={samletReachedGoal2}
        />
      </div>

      {/* De 4 KPI cirkler */}
      <div className="flex justify-center gap-4 flex-wrap">

        {/* Fremmøde */}
        <KPICircle
          mode="simple" goalMode={true}
          label="Fremmøde"
          centerValue={`${att.pct}%`}
          percentage={attCirclePct}
          goal1Pct={attGoal1RingPct} goal2Pct={100}
          reachedGoal1={att.reached_goal_1} reachedGoal2={att.reached_goal_2}
        />

        {/* Opgraderingsydelser */}
        {upgHasData ? (
          <KPICircle
            mode="simple" goalMode={true}
            label="Opgraderinger"
            centerValue={`${upgPct}%`}
            percentage={upgCirclePct}
            goal1Pct={upgGoal1RingPct} goal2Pct={100}
            reachedGoal1={upg.reached_goal_1} reachedGoal2={upg.reached_goal_2}
          />
        ) : (
          <KPICircle mode="placeholder" label="Opgraderinger" centerValue="—" percentage={0} />
        )}

        {/* Produktsalg */}
        <KPICircle
          mode="simple" goalMode={true}
          label="Produktsalg"
          centerValue={fmt(prod.revenue).replace(" kr.", "").replace(" kr", "")}
          percentage={prodCirclePct}
          goal1Pct={prodGoal1RingPct} goal2Pct={100}
          reachedGoal1={prod.reached_goal_1} reachedGoal2={prod.reached_goal_2}
        />

        {/* Gavekort og klippekort */}
        <KPICircle
          mode="simple" goalMode={true}
          label="Gavekort & klippekort"
          centerValue={fmt(gv.revenue).replace(" kr.", "").replace(" kr", "")}
          percentage={gvCirclePct}
          goal1Pct={gvGoal1RingPct} goal2Pct={100}
          reachedGoal1={gv.reached_goal_1} reachedGoal2={gv.reached_goal_2}
        />

      </div>

      {/* Mål-bokse */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

        {/* Fremmøde */}
        <KPICard title="Fremmøde" reached={att.reached_goal_1}>
          <div className="text-center mb-3">
            <span className="text-2xl font-extrabold" style={{ color: attColorForBar }}>{att.pct}%</span>
          </div>
          <GoalBar value={att.pct} goal1={att.goal_1} goal2={att.goal_2} format="pct" />
          <div className="mt-3 space-y-2">
            <BonusCard reached={att.reached_goal_1} label="1. mål" goal={att.goal_1} format="pct" />
            <BonusCard reached={att.reached_goal_2} label="2. mål" goal={att.goal_2} format="pct" />
          </div>
          <p className="text-[13px] text-muted-foreground mt-3 leading-relaxed">
          </p>
        </KPICard>

        {/* Opgraderingsydelser */}
        <KPICard title="Opgraderingsydelser" reached={upg.reached_goal_1}>
          {upgHasData ? (
            <>
              <div className="text-center mb-3">
                <span className="text-2xl font-extrabold text-foreground">{upgPct}%</span>
                <p className="text-[13px] text-muted-foreground">{upg.med_opgradering} af {upg.behandlinger} kunder har fået opgradering</p>
              </div>
              <GoalBar value={upgPct} goal1={upg.goal_1} goal2={upg.goal_2} format="pct" />
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">Data mangler endnu</p>
              <p className="text-[13px] text-muted-foreground mt-1">Behandlingsdata hentes løbende</p>
            </div>
          )}
          <div className="mt-3 space-y-2">
            <BonusCard reached={upg.reached_goal_1} label="1. mål" goal={upg.goal_1} format="pct" extra={`— Svarer til ${Math.round(upg.goal_1 / 10)} ud af 10 kunder`} />
            <BonusCard reached={upg.reached_goal_2} label="2. mål" goal={upg.goal_2} format="pct" extra={`— Svarer til ${Math.round(upg.goal_2 / 10)} ud af 10 kunder`} />
          </div>
        </KPICard>

        {/* Produktsalg */}
        <KPICard title="Produktsalg" reached={prod.reached_goal_1}>
          <div className="text-center mb-3">
            <span className="text-2xl font-extrabold text-foreground">{fmt(prod.revenue)}</span>
          </div>
          <GoalBar value={prod.revenue} goal1={prod.goal_1} goal2={prod.goal_2} format="kr" />
          <div className="mt-3 space-y-2">
            <BonusCard reached={prod.reached_goal_1} label="1. mål" goal={prod.goal_1} format="kr" />
            <BonusCard reached={prod.reached_goal_2} label="2. mål" goal={prod.goal_2} format="kr" />
          </div>
        </KPICard>

        {/* Gavekort og klippekort */}
        <KPICard title="Gavekort & klippekort" reached={gv.reached_goal_1}>
          <div className="text-center mb-3">
            <span className="text-2xl font-extrabold text-foreground">{fmt(gv.revenue)}</span>
          </div>
          <GoalBar value={gv.revenue} goal1={gv.goal_1} goal2={gv.goal_2} format="kr" />
          <div className="mt-3 space-y-2">
            <BonusCard reached={gv.reached_goal_1} label="1. mål" goal={gv.goal_1} format="kr" />
            <BonusCard reached={gv.reached_goal_2} label="2. mål" goal={gv.goal_2} format="kr" />
          </div>
        </KPICard>

      </div>
    </div>
  );
}
